import { ApplicationElement, html } from '@effectivastudio/lit-components';
import translations from './translations.js';
import style from './style.lit.css';
import '../feed_menu/element.js';

/**
 * Element for feed import source
 *
 * - organizerUrl for RSS and iWeb
 * - organizerId for EventFrog
 *
 * Check iWeb, RSS and EventFrog examples for full attribute list for each source.
 *
 * MENU:
 * - optional
 * - if the component has menu, groupId, type and at least one of editOn/updateOn/deleteOn options are obligatory
 *
 * @prop name - Feed title
 * @prop uid - Feed id
 * @prop organizerUrl - Feed organizer URL
 * @prop organizerId - Id of event organizer
 * @prop status - Feed sync status (options: uninitialized, processing, working, inactive, broken)
 * @prop count - Feed cards count
 * @prop streamName - Name of stream where the feeds will be displayed
 * @prop streamUrl - Url of the stream where the feeds will be displayed
 * @prop updatedAt - Feed last sync date
 *
 * Menu
 * @prop groupId - Id of the group where the feed is imported
 * @prop type - The type of source (options: event_frog, rss, iweb)
 * @prop editOn - Is edit option for feed available?
 * @prop updateOn - Is update option for feed available?
 * @prop deleteOn - Is delete option for feed available?
 *
 */

export default class Feed extends ApplicationElement {
  static styles = [ApplicationElement.styles, style];

  static properties = {
    name: { type: String },
    uid: { type: Number },
    organizerUrl: { type: String },
    organizerId: { type: String },
    status: { type: String },
    count: { type: Number },
    streamName: { type: String },
    streamUrl: { type: String },
    groupId: { type: Number },
    updatedAt: { type: Date },
    type: { type: String },
    editOn: { type: Boolean },
    updateOn: { type: Boolean },
    deleteOn: { type: Boolean },
  };

  /** @internal */
  STATUS = {
    uninitialized: 'uninitialized',
    processing: 'processing',
    working: 'working',
    inactive: 'inactive',
    broken: 'broken',
  };

  /** @internal */
  ICONS = {
    uninitialized: 'watch',
    processing: 'watch',
    working: 'check',
    inactive: 'octagon-exclamation',
    broken: 'close',
  };

  constructor() {
    super();
    this.status = this.STATUS.uninitialized;
  }

  connectedCallback() {
    super.connectedCallback();
    this.channel = window.pusher?.subscribe(this.channelId);
    this.channel && this.setupPusherEvents();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.channel?.unsubscribe();
  }

  /** @internal */
  static translations = translations;

  /** @internal */
  get channelId() {
    return `group-${this.uid}-import`;
  }

  /** @internal */
  get hasMenu() {
    return this.updateOn || this.editOn || this.deleteOn;
  }

  /** @internal */
  get icon() {
    return this.ICONS[this.status];
  }

  handleUpdate(data) {
    if (data.progress === 'started') {
      this.status = this.STATUS.processing;
    } else if (data.progress === 'done') {
      this.status = data.schedule_status;
      this.updatedAt = Date.now();
      this.count = data.card_count;
    }
  }

  setupPusherEvents() {
    this.channel.bind('update', this.handleUpdate.bind(this));
  }

  renderMenu() {
    return html`
      <xs-feed-menu
        @feed-deleted=${this.remove}
        .updateOn=${this.updateOn}
        .editOn=${this.editOn}
        .deleteOn=${this.deleteOn}
        .feedId=${this.uid}
        .groupId=${this.groupId}
        .feedType=${this.type}>
      </xs-feed-menu>
    `;
  }

  renderLastUpdate() {
    return html`
      <div>
        ${this.t('updated')}:
        <ef-timeago .timestamp=${this.updatedAt}></ef-timeago>
      </div>
    `;
  }

  render() {
    return html`
      <header>
        <div class="main-header">
          <h4 class="xs-title-3">${this.name}</h4>
          ${this.hasMenu ? this.renderMenu() : null}
        </div>
        <div class="sub-header">
          ${this.organizerUrl
            ? `${this.t('url')}: ${this.organizerUrl}`
            : `${this.t('organizer')}: ${this.organizerId}`}
        </div>
      </header>
      <main class=${this.status}>
        <ef-icon name=${this.icon}></ef-icon>
        ${this.t(`status.${this.status}`)}
      </main>
      <footer>
        <div>${this.t('cards')}: ${this.count || 0}</div>
        <div>
          ${this.t('location')}:
          <a href=${this.streamUrl}>
            ${this.t(`posting_location.${this.streamName}`)}
          </a>
        </div>
        ${this.updatedAt ? this.renderLastUpdate() : null}
      </footer>
    `;
  }
}

Feed.registerTagName('xs-feed');

customElements.get('xs-feed') || customElements.define('xs-feed', Feed);
